import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  createMissionEndAPI,
  getMissionEndAPI,
  updateMissionEndAPI,
} from "../../../../../api/index";
import "../AdDetailModal.css";
import "./AdEndRequestModal.css";
import { isSpecialUser } from "../../../../../utils/environment";
import WeekdayOnlyDatePicker from "../../addAdvertisement/components/WeekdayOnlyDatePicker";

const AdEndRequestModal = ({
  isOpen,
  onClose,
  adData,
  onSave,
  user,
  endSubmitTime = "16:00",
}) => {
  const [appliedDate, setAppliedDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [pendingRequestList, setPendingRequestList] = useState([]);
  const [pendingCheckLoading, setPendingCheckLoading] = useState(false);
  const userIsSpecial = isSpecialUser(user?.id);

  // 모달 열릴 때 해당 광고의 PENDING 광고 종료 신청 존재 여부 조회
  useEffect(() => {
    if (!isOpen || !adData?.ad_id) {
      setHasPendingRequest(false);
      setPendingRequestList([]);
      return;
    }
    let cancelled = false;
    setPendingCheckLoading(true);
    getMissionEndAPI({
      ad_id: adData.ad_id,
      webMemberId: adData.webMemberId ?? adData.member_id,
      status: "PENDING",
    })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setPendingRequestList(list);
        setHasPendingRequest(list.length > 0);
      })
      .catch(() => {
        if (!cancelled) {
          setHasPendingRequest(false);
          setPendingRequestList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setPendingCheckLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, adData?.ad_id]);

  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  const requesterUsername =
    adData?.username ||
    adData?.member_id ||
    user?.username ||
    (user?.id ? String(user.id) : "");
  const requesterName =
    adData?.name ||
    user?.name ||
    adData?.username ||
    requesterUsername ||
    "미지정";

  // end_submit 파싱 ("16:00" → 시, 분)
  const endSubmitParsed = useMemo(() => {
    const match = (endSubmitTime || "16:00").toString().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return { hour: 16, minute: 0 };
    return {
      hour: parseInt(match[1], 10),
      minute: parseInt(match[2], 10),
    };
  }, [endSubmitTime]);

  // 희망 종료일 최소 선택일 (한국 시간 기준, all_zoom_setting.end_submit 기준)
  // 평일 end_submit 전: 다음날부터 / 평일 end_submit 이후: 그다음날부터 / 주말: 화요일부터
  const defaultMinDateStr = useMemo(() => {
    const now = new Date();
    const kstOffset = 9 * 60 * 60 * 1000;
    const kst = new Date(now.getTime() + kstOffset);
    const kstDay = kst.getUTCDay();
    const kstHours = kst.getUTCHours();
    const kstMinutes = kst.getUTCMinutes();
    const kstDate = kst.getUTCDate();
    const kstMonth = kst.getUTCMonth();
    const kstYear = kst.getUTCFullYear();
    const { hour: cutoffHour, minute: cutoffMinute } = endSubmitParsed;

    let daysToAdd = 1;
    if (kstDay === 0 || kstDay === 6) {
      daysToAdd = kstDay === 6 ? 3 : 2;
    } else if (
      kstHours > cutoffHour ||
      (kstHours === cutoffHour && kstMinutes >= cutoffMinute)
    ) {
      daysToAdd = 2;
    }

    const minDate = new Date(Date.UTC(kstYear, kstMonth, kstDate));
    minDate.setUTCDate(minDate.getUTCDate() + daysToAdd);
    const y = minDate.getUTCFullYear();
    const m = String(minDate.getUTCMonth() + 1).padStart(2, "0");
    const d = String(minDate.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [endSubmitParsed]);
  const effectiveMinDateStr = userIsSpecial ? todayStr : defaultMinDateStr;

  // 광고 종료일 전날 (최대 선택일) — 년월일만 사용 (UTC 아님)
  const maxDateStr = useMemo(() => {
    const dateOnly = adData?.endDate?.split("T")[0];
    if (!dateOnly) return null;
    const [y, m, d] = dateOnly.split("-").map(Number);
    const endDateOnly = new Date(y, m - 1, d);
    endDateOnly.setDate(endDateOnly.getDate() - 1);
    const y2 = endDateOnly.getFullYear();
    const m2 = String(endDateOnly.getMonth() + 1).padStart(2, "0");
    const d2 = String(endDateOnly.getDate()).padStart(2, "0");
    return `${y2}-${m2}-${d2}`;
  }, [adData?.endDate]);

  // 하루 금액: price * dailyQuantity / totalQuantity
  const dailyPrice = useMemo(() => {
    const price = adData?.price || 0;
    const totalQuantity = adData?.totalQuantity || 0;
    const dailyQuantity = adData?.dailyQuantity || 0;
    if (totalQuantity <= 0) return 0;
    return Math.round((price * dailyQuantity) / totalQuantity);
  }, [adData?.price, adData?.totalQuantity, adData?.dailyQuantity]);

  const unitPrice = useMemo(() => {
    const totalQuantity = adData?.totalQuantity || 0;
    if (totalQuantity <= 0) return 0;
    return Math.round((adData?.price || 0) / totalQuantity);
  }, [adData?.price, adData?.totalQuantity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!appliedDate) {
      setError("희망 종료일을 선택해주세요.");
      return;
    }
    const minD = effectiveMinDateStr
      ? new Date(effectiveMinDateStr + "T00:00:00")
      : null;
    const maxD = maxDateStr ? new Date(maxDateStr + "T00:00:00") : null;
    const selected = new Date(appliedDate + "T00:00:00");
    if (selected < minD) {
      setError("희망 종료일은 선택 가능 기간 내에서 선택해 주세요.");
      return;
    }
    if (!userIsSpecial) {
      const day = selected.getDay();
      if (day === 0 || day === 6) {
        setError("희망 종료일은 평일만 선택 가능합니다.");
        return;
      }
    }
    if (maxD && selected > maxD) {
      setError("희망 종료일은 광고 종료일 전날까지 선택 가능합니다.");
      return;
    }
    if (hasPendingRequest) {
      const ok = window.confirm(
        "이미 광고 종료 신청이 존재합니다. 신청하시면 기존 건은 취소되고 새로 신청됩니다. 진행하시겠습니까?"
      );
      if (!ok) return;
    }

    try {
      setIsSubmitting(true);

      // 기존 PENDING 건 취소 — { id, status: "CANCELLED" } 형태로 PATCH
      if (pendingRequestList.length > 0) {
        await Promise.all(
          pendingRequestList.map((item) =>
            updateMissionEndAPI({
              id: item.id ?? item._id,
              status: "CANCELLED",
            })
          )
        );
      }

      // 년월일만 사용 (UTC 아님)
      const startStr = adData.startDate?.split("T")[0] || "";
      const startDateOnly = new Date(startStr + "T00:00:00");
      const newEndDateOnly = new Date(appliedDate + "T00:00:00");
      const daysDiff = Math.round(
        (newEndDateOnly - startDateOnly) / (24 * 60 * 60 * 1000)
      );
      const daysCount = daysDiff + 1;
      const dailyQuantity = adData.dailyQuantity || 0;
      const newTotalQuantity = daysCount * dailyQuantity;
      const newPrice = newTotalQuantity * unitPrice;

      // 선택한 날짜·원래 종료일 모두 UTC 자정 ISO로 전달 (날짜만 오면 T00:00:00.000Z 붙임)
      const newEndDateISO = appliedDate + "T00:00:00.000Z";
      const rawEnd = adData.endDate ?? "";
      const beforeEndDateISO =
        rawEnd && !rawEnd.includes("T")
          ? rawEnd + "T00:00:00.000Z"
          : rawEnd;

      const payload = {
        ad_id: adData.ad_id || adData.id,
        webMemberId: adData.webMemberId || adData.member_id,
        appliedDate: newEndDateISO,
        username: requesterUsername,
        name: requesterName,
        missionName: adData.missionName || adData.adName || "광고",
        missionType: adData.missionType ?? 1,
        missionTemplate: adData.missionTemplate ?? 1,
        status: "PENDING",
        beforeEndDate: beforeEndDateISO,
        newEndDate: newEndDateISO,
        dailyPrice: dailyPrice,
        adminMemo: "",
        showMemo: "",
        data: {
          price: newPrice,
          totalQuantity: newTotalQuantity,
          dailyQuantity: dailyQuantity,
          endDate: newEndDateISO,
        },
        adminId: user?.id ?? null,
      };

      await createMissionEndAPI(payload);
      alert("광고 종료 신청이 완료되었습니다.");
      onClose();
      if (onSave) onSave();
    } catch (err) {
      console.error("광고 종료 신청 실패:", err);
      setError(
        err?.response?.data?.message || "광고 종료 신청에 실패했습니다. 다시 시도해주세요."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const content = (
    <div className="ad-end-request-modal-overlay" onClick={onClose}>
      <div
        className="ad-end-request-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ad-end-request-modal-header">
          <h3>광고 종료 신청</h3>
          <button
            type="button"
            className="ad-end-request-modal-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="ad-end-request-modal-body">
          <div className="ad-end-request-modal-desc">
            <p className="ad-end-request-modal-desc-title">
              희망 종료일을 선택해 주세요.
            </p>
            <p className="ad-end-request-modal-desc-note">
              <strong>선택하신 희망 종료일까지 광고가 진행됩니다.</strong>
            </p>
            <p className="ad-end-request-modal-desc-range">
              선택 가능 기간: {effectiveMinDateStr} ~ 광고 종료일 전날
            </p>
            {!userIsSpecial && (
              <div className="ad-end-request-modal-desc-reason">
                <p className="ad-end-request-modal-desc-reason-title">
                  선택 가능한 최소 일자 (신청 시각 기준)
                </p>
                <ul className="ad-end-request-modal-desc-reason-list">
                  <li>
                    평일 {endSubmitTime || "16:00"} 전 신청 → 다음날부터 선택
                    가능
                  </li>
                  <li>
                    평일 {endSubmitTime || "16:00"} 이후 신청 → 그다음날부터
                    선택 가능
                  </li>
                  <li>주말 신청 → 월요일 확인 후 화요일부터 선택 가능</li>
                </ul>
                <p className="ad-end-request-modal-desc-reason-weekday">
                  희망 종료일은 평일(월~금)만 선택할 수 있습니다.
                </p>
              </div>
            )}
          </div>
          {pendingCheckLoading ? (
            <div className="ad-end-request-modal-pending-check">
              확인 중...
            </div>
          ) : hasPendingRequest ? (
            <div className="ad-end-request-modal-pending-notice">
              이미 광고 종료 신청이 존재합니다. 신청하시면 기존 건은 취소되고
              새로 신청됩니다.
            </div>
          ) : null}
          <div className="ad-end-request-modal-field">
            <label htmlFor="appliedDate">희망 광고 종료일</label>
            <WeekdayOnlyDatePicker
              value={appliedDate}
              onChange={setAppliedDate}
              minDate={effectiveMinDateStr || undefined}
              maxDate={maxDateStr || undefined}
              disabled={isSubmitting}
              placeholderText="날짜 선택"
              weekdaysOnly={true}
              userId={user?.id}
            />
          </div>
          {error && <p className="ad-end-request-modal-error">{error}</p>}
          <div className="ad-end-request-modal-actions">
            <button
              type="button"
              className="ad-end-request-modal-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              취소
            </button>
            <button
              type="submit"
              className="ad-end-request-modal-submit"
              disabled={isSubmitting || !appliedDate}
            >
              {isSubmitting ? "신청 중..." : "신청하기"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default AdEndRequestModal;
